import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { TimezoneDisplayMode } from '@/utils/timezoneAdvanced'
import { useUser } from '@/app/store/auth'

interface TimezoneDisplayContextType {
    displayMode: TimezoneDisplayMode
    setDisplayMode: (mode: TimezoneDisplayMode) => void
}

const TimezoneDisplayContext = createContext<TimezoneDisplayContextType | undefined>(undefined)

interface TimezoneDisplayProviderProps {
    children: ReactNode
}

export function TimezoneDisplayProvider({ children }: TimezoneDisplayProviderProps) {
    const user = useUser()
    
    // Initialize from localStorage first, then default to user's timezone setting
    const getInitialDisplayMode = (): TimezoneDisplayMode => {
        // Check localStorage first
        const stored = localStorage.getItem('timezoneDisplayMode')
        if (stored && ['local', 'timezone', 'utc'].includes(stored)) {
            return stored as TimezoneDisplayMode
        }
        
        // Default to 'timezone' (user's profile timezone setting)
        return 'timezone'
    }
    
    const [displayMode, setDisplayMode] = useState<TimezoneDisplayMode>(getInitialDisplayMode)

    // Update localStorage when display mode changes
    useEffect(() => {
        localStorage.setItem('timezoneDisplayMode', displayMode)
    }, [displayMode])

    return (
        <TimezoneDisplayContext.Provider value={{ displayMode, setDisplayMode }}>
            {children}
        </TimezoneDisplayContext.Provider>
    )
}

export function useTimezoneDisplay() {
    const context = useContext(TimezoneDisplayContext)
    if (context === undefined) {
        throw new Error('useTimezoneDisplay must be used within a TimezoneDisplayProvider')
    }
    return context
}











